import { PencilIcon } from '@heroicons/react/20/solid';
import React from 'react';

import { Box } from '@components/Box';
import { FormikSelectOption } from '@models/common/FormikSelectOption';
import { CustomerViewModel } from '@models/Customer';

export interface CustomerDisplayCardProps {
  selectedCustomer: CustomerViewModel;
  contactID?: number;
  contactEmail?: string;
  customerContacts: FormikSelectOption[];
  onEditClick: () => void;
  className?: string;
}

/**
 * CustomerDisplayCard Component
 *
 * A reusable component that displays customer information in a calling card format.
 * Used when the customer form is collapsed to show a summary of selected customer details.
 *
 * @param selectedCustomer - The selected customer object containing company details
 * @param contactID - The ID of the selected contact (optional)
 * @param contactEmail - The email of the selected contact (optional)
 * @param customerContacts - Array of contact options to find contact name
 * @param onEditClick - Callback function when edit button is clicked
 * @param className - Additional CSS classes (optional)
 */
export const CustomerDisplayCard: React.FC<CustomerDisplayCardProps> = ({
  selectedCustomer,
  contactID,
  contactEmail,
  customerContacts,
  onEditClick,
  className = '',
}) => {
  return (
    <div
      className={`mb-6 transition-all duration-300 ease-in-out ${className}`}
    >
      <Box size="full">
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-primary-500 p-6 shadow-sm relative">
          {/* Edit Button */}
          <button
            type="button"
            onClick={onEditClick}
            className="absolute top-4 right-4 bg-white border border-gray-300 rounded-full p-1 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <PencilIcon className="h-4 w-4 text-gray-600" />
          </button>

          <div className="flex items-start justify-between pr-12">
            <div className="flex-1">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                    <svg
                      className="h-6 w-6 text-primary-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="space-y-1">
                    <h3 className="text-lg font-semibold text-gray-900 truncate">
                      {selectedCustomer.companyName}
                    </h3>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      {contactID && contactID !== 0 && (
                        <>
                          <div className="flex items-center">
                            <svg
                              className="h-4 w-4 mr-1 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                              />
                            </svg>
                            <span className="font-medium">
                              {
                                customerContacts.find(
                                  (c) => c.value === String(contactID)
                                )?.text
                              }
                            </span>
                          </div>
                          {contactEmail && (
                            <div className="flex items-center">
                              <svg
                                className="h-4 w-4 mr-1 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2v10a2 2 0 002 2z"
                                />
                              </svg>
                              <span className="text-gray-600">
                                {contactEmail}
                              </span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex-shrink-0 ml-4">
              <div className="text-right">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Customer ID
                </div>
                <div className="text-sm font-semibold text-gray-900">
                  #{selectedCustomer.id}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Box>
    </div>
  );
};
